/**
 * useFiduciaryDerivation — Fiduciary entity-type derivation engine.
 *
 * Within the Fiduciary entity group, `customerType` is MANUALLY selected from
 * the 3 (currently-scoped) sub-types — RepPayee, Conservator, VAFiduciary —
 * rather than auto-derived from DOB/citizenship (Individual's rule).
 *
 * This hook mirrors the `DerivationResult` contract from
 * `useIndividualDerivation` so shared Identity/CIP section props stay uniform.
 *
 * Rules:
 *   1. baseline = stored customerType when existing, else 'RepPayee' default.
 *   2. The sub-type pick in FiduciaryIdentitySection writes back into
 *      person.customerType via onChange(); this hook merely surfaces it +
 *      flags supervisor approval.
 *   3. `requiresSupervisorApproval` trips when:
 *      - sub-type is in SUPERVISOR_APPROVAL_SUBTYPES (Conservator / VAFiduciary), OR
 *      - appointment authority differs from the sub-type's canonicalAuthority
 *        (e.g., Conservator with SSA authority — atypical, needs review).
 *
 * TODO(bill): fiduciary walkthrough needed — confirm:
 *   - Are MSA / USDA approvals supervisor-gated? (seed rows missing per card)
 *   - Does promotion between fiduciary sub-types (e.g. RepPayee → Conservator
 *     on an existing CIF) require supervisor signoff even when neither is in
 *     the approval set, similar to Individual's auto-promote flow?
 */

import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { RaoPerson } from '../../../types';
import type { DerivationResult } from '../individual/useIndividualDerivation';
import {
  FIDUCIARY_SUB_TYPE_BY_VALUE,
  SUPERVISOR_APPROVAL_SUBTYPES,
  type FiduciaryAppointmentAuthority,
} from './fiduciary-config';

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Fiduciary customer types currently scoped. String type (not the
 * shared-types CustomerType enum) because those values aren't in the enum yet —
 * TODO(bill): lift to CustomerType enum when shared-types wave-3 lands.
 */
export type FiduciaryCustomerType = 'RepPayee' | 'Conservator' | 'VAFiduciary';

export interface UseFiduciaryDerivationOptions {
  /** 'new' = fresh capture; 'existing' = CustInq reload of pre-existing CIF. */
  readonly mode: 'new' | 'existing';
  /** The customerType that Core Director had on file for this CIF (existing only). */
  readonly storedType?: string;
}

// -----------------------------------------------------------------------------
// Officer supervisor-role probe — same shape as Individual hook so behaviour
// stays aligned. Pulled inline to avoid cross-importing an internal util.
// -----------------------------------------------------------------------------

function isSupervisor(auth: ReturnType<typeof useAuth>): boolean {
  const user: unknown = (auth as unknown as { user?: unknown })?.user;
  if (!user || typeof user !== 'object') return false;
  const u = user as Record<string, unknown>;
  if (u.isSupervisor === true) return true;
  if (typeof u.role === 'string' && u.role.toLowerCase().includes('supervisor')) return true;
  const roles = u.roles;
  if (Array.isArray(roles)) {
    return (roles as unknown[]).some(
      (r) => typeof r === 'string' && r.toLowerCase().includes('supervisor'),
    );
  }
  return false;
}

// -----------------------------------------------------------------------------
// Extra-field helpers — appointmentAuthority + subType live on `person.extra`
// until shared-types wave-3 promotes them.
// -----------------------------------------------------------------------------

function readExtraString(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Compute the derived customerType + supervisor-gate for the given Fiduciary.
 *
 * Memoized on the Person inputs that actually influence the result
 * (customerType + extra.appointmentAuthority) so re-renders caused by
 * unrelated field edits don't thrash.
 */
export function useFiduciaryDerivation(
  person: RaoPerson,
  opts: UseFiduciaryDerivationOptions,
): DerivationResult {
  const auth = useAuth();
  const appointmentAuthority = readExtraString(person, 'appointmentAuthority');

  return useMemo<DerivationResult>(() => {
    const officerIsSupervisor = isSupervisor(auth);

    // Rule 1: baseline — use the stored/selected customerType if valid,
    // otherwise default to RepPayee (most common, lowest-friction).
    const picked =
      (person.customerType && FIDUCIARY_SUB_TYPE_BY_VALUE[person.customerType]) || null;
    const customerType: FiduciaryCustomerType =
      (picked?.value as FiduciaryCustomerType) ??
      (opts.mode === 'existing' && opts.storedType
        ? (opts.storedType as FiduciaryCustomerType)
        : 'RepPayee');

    // Rule 3a: sub-type is in the supervisor-approval set.
    const subTypeGated = SUPERVISOR_APPROVAL_SUBTYPES.has(customerType);

    // Rule 3b: appointment authority mismatches the sub-type's canonical
    // authority — atypical pairing, needs review.
    const canonicalAuthority: FiduciaryAppointmentAuthority | undefined =
      FIDUCIARY_SUB_TYPE_BY_VALUE[customerType]?.canonicalAuthority;
    const authorityMismatch =
      !!appointmentAuthority &&
      !!canonicalAuthority &&
      appointmentAuthority !== canonicalAuthority;

    const requiresSupervisorApproval =
      (subTypeGated || authorityMismatch) && !officerIsSupervisor;

    // Existing-CIF reload — if stored type differs from picked, surface
    // `promotedFrom` so the UI shows the "updated from 'X'" note, mirroring
    // Individual's behaviour.
    const promotedFrom =
      opts.mode === 'existing' && opts.storedType && opts.storedType !== customerType
        ? opts.storedType
        : undefined;

    return {
      customerType: customerType as unknown as DerivationResult['customerType'],
      derivedFrom: 'baseline',
      ...(promotedFrom ? { promotedFrom } : {}),
      ...(requiresSupervisorApproval ? { requiresSupervisorApproval: true } : {}),
    };
  }, [person.customerType, appointmentAuthority, opts.mode, opts.storedType, auth]);
}
